import { ArrowRightIcon, CopyIcon, MapPinIcon, NavigationIcon } from 'lucide-react';
import { toast } from 'sonner';
import { HeadingText } from '../shared/HeadingText';
import { SalonMap } from './SalonMap';
import { hoursLabel, salon } from '../../data/content';
import { usePages, useSite } from '../../contexts/SiteContext';
import { shopLabels } from '../../data/shopLabels';
import { pageActions } from '../../data/pageActions';
import { isOpen } from '../../core/rules/schedule';
import { salonWeekday } from '../../core/format/dates';
import { useOverlayActions } from '../../contexts/StoreContext';
import { fill } from '../../utils/copy';
import { btnPrimary, btnSecondary } from '../../ui/styles';
import { sectionHeading } from '../../ui/sections';
import { Section } from '../shared/Section';

/** Id van dit blok: de hero linkt hierheen ("Route en kaart"). */
export const VISIT_ID = 'langskomen';

/**
 * Langskomen: alles wat een nieuwe cliënt vóór haar eerste afspraak wil weten.
 * Volgorde = de vragen die ze in die volgorde stelt: waar is het, hoe kom ik er,
 * wanneer kan ik komen, en dan pas: plan een afspraak.
 */
export function ContactVisit() {
  const pages = usePages();
  const {
    openingHours
  } = useSite();
  const {
    openBooking
  } = useOverlayActions();
  const copy = pages.contact.visit;
  const today = salonWeekday();
  const todayHours = openingHours.find(h => h.weekday === today);
  const address = salon.address || `${salon.street}, ${salon.postalCode} ${salon.locality}`;
  const arrival = [{
    label: 'Openbaar vervoer',
    text: salon.transit
  }, {
    label: 'Parkeren',
    text: salon.parking
  }, {
    label: 'Aanbellen',
    text: salon.entrance
  }, {
    label: 'Toegankelijkheid',
    text: salon.accessibility
  }].filter(a => a.text?.trim());
  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(address);
      toast.success('Adres gekopieerd', {
        description: address
      });
    } catch {
      toast.error('Kopiëren lukte niet', {
        description: address
      });
    }
  };
  return <Section id={VISIT_ID} tone="blush" sectionClassName="scroll-mt-28" labelledBy="contact-visit" contained={false} data-id="element-7443" data-mp="3nugxg3o4b7v5">
      <div className="mx-auto grid max-w-content gap-10 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-7444" data-mp="3juyal2cqkmpo">
        {/* Tekst eerst in de bron: op mobiel lees je eerst waar het is, dan zie je de kaart. */}
        <div className="lg:col-span-5 lg:col-start-8 lg:row-start-1" data-id="element-7445" data-mp="3stjq0omwvlm8">
          {/* Op blush haalt kleine clay-tekst geen AA (4,3:1): het regeltje staat in ink. */}
          {copy.eyebrow && <p className="text-[14px] text-ink" data-id="element-7446" data-mp="6fjtk8nl6uts">{fill(copy.eyebrow)}</p>}
          <h2 id="contact-visit" className={`mt-4 ${sectionHeading} text-ink`} data-id="element-7447" data-mp="kejhfe39z5ow">
            <HeadingText heading={copy.heading} data-id="element-7448" data-mp="kcff30l0yeqq" />
          </h2>
          {/* "Alleen op afspraak" is één vaste formulering (ook bij kom langs en op Salon); de zin erna is van Sarah. */}
          <p className="mt-6 max-w-md text-[17px] leading-relaxed text-muted" data-id="element-7449" data-mp="2m650rzh0kqy9">
            <span className="text-ink" data-id="element-7450" data-mp="2h83fr9012hk5">{pageActions.byAppointment}.</span>
            {copy.body && <> {fill(copy.body)}</>}
          </p>

          <address className="mt-8 flex gap-3 not-italic" data-id="element-7451" data-mp="wr3uizlar51n">
            <MapPinIcon className="mt-1 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7452" data-mp="11mj6flzquqvf" />
            <div data-id="element-7453" data-mp="nvxs8al8j53v">
              <p className="text-[17px] leading-snug text-ink" data-id="element-7454" data-mp="nucyuoanps7l">
                {salon.street}
                <br data-id="element-7455" data-mp="rvy5vt0ifc9v" />
                {salon.postalCode} {salon.locality}
              </p>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-[14px]" data-id="element-7456" data-mp="dwyjks0gz9gv">
                <a href={salon.routeUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 py-1 text-ink underline-offset-4 transition-colors duration-150 ease-soft hover:text-clay hover:underline" data-id="element-7457" data-mp="iy5u2ytzymr8">
                  <NavigationIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7458" data-mp="1z9i1xtep9srq" />
                  Plan je route<span className="sr-only" data-id="element-7459" data-mp="23av9c7xfy40r"> {shopLabels.newTab}</span>
                </a>
                <button type="button" onClick={() => void copyAddress()} className="inline-flex items-center gap-1.5 py-1 text-muted underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink hover:underline" data-id="element-7460" data-mp="1ekh64gh43sij">
                  <CopyIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7461" data-mp="zrdns6vp5aqr" />
                  Kopieer adres
                </button>
              </div>
            </div>
          </address>

          {arrival.length > 0 && <div className="mt-10" data-id="element-7462" data-mp="lxczk72df64n">
              {copy.arriveTitle && <h3 className="type-name text-ink" data-id="element-7463" data-mp="lvs50hg23n6s">{fill(copy.arriveTitle)}</h3>}
              <dl className="mt-4 divide-y divide-ink/15 border-y border-ink/15" data-id="element-7464" data-mp="h0cu9ydci7el">
                {arrival.map(a => <div key={a.label} className="grid gap-1 py-4 sm:grid-cols-[9.5rem_1fr] sm:gap-4" data-id="element-7465" data-mp="c1t7jvqxgrl6">
                    <dt className="text-[14px] text-muted" data-id="element-7466" data-mp="80hchapabe5l">{a.label}</dt>
                    <dd className="text-[15px] leading-relaxed text-ink" data-id="element-7467" data-mp="2jkx7cian0bgg">{a.text}</dd>
                  </div>)}
              </dl>
            </div>}

          {copy.cta && <div className="mt-8 flex flex-wrap gap-3" data-id="element-7468" data-mp="2smohbzc7n4g4">
              <button type="button" onClick={() => openBooking()} aria-haspopup="dialog" className={btnPrimary} data-id="element-7469" data-mp="2xlrk1d4pwykx">
                {fill(copy.cta)}
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7470" data-mp="1zzanwb7q0r0r" />
              </button>
              {copy.secondaryCta && <a href={salon.whatsapp} target="_blank" rel="noopener noreferrer" className={btnSecondary} data-id="element-7471" data-mp="29v5fhvtx9wfo">
                  {fill(copy.secondaryCta)}
                  <span className="sr-only" data-id="element-7472" data-mp="2eogl5tn00760">{shopLabels.viaWhatsApp}</span>
                </a>}
            </div>}
        </div>

        <div className="lg:col-span-7 lg:col-start-1 lg:row-start-1" data-id="element-7473" data-mp="1m5azj5eb3rr7">
          <SalonMap loadLabel={fill(copy.mapCta || 'Toon de kaart')} note={fill(copy.mapNote || '')} data-id="element-7474" data-mp="1e5ddoeeyzbpa" />

          <div className="mt-8" data-id="element-7475" data-mp="1a41ky00suacv">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1" data-id="element-7476" data-mp="1a2ql2bah5mr1">
              <h3 className="type-name text-ink" data-id="element-7477" data-mp="1nu51caczz9ue">Openingstijden · op afspraak</h3>
              {todayHours && <p className="text-[14px] text-muted" data-id="element-7478" data-mp="1ixelno53ug9i">
                  Vandaag: <span className="text-ink" data-id="element-7479" data-mp="v5zf1j6apcsg">{isOpen(todayHours) ? hoursLabel(todayHours) : 'gesloten'}</span>
                </p>}
            </div>
            {/* Twee kolommen op desktop: zeven regels passen naast elkaar en de kaart blijft het grootst. */}
            <dl className="mt-4 grid border-t border-ink/15 text-[15px] sm:grid-cols-2 sm:gap-x-10" data-id="element-7480" data-mp="152caomlei0pu">
              {openingHours.map(h => {
              const isToday = h.weekday === today;
              const open = isOpen(h);
              return <div key={h.day} className={`flex justify-between gap-4 border-b border-ink/15 py-3 ${isToday ? 'font-medium' : ''}`} aria-current={isToday ? 'date' : undefined} data-id="element-7481" data-mp="2bk94yhq6r9le">
                    <dt className={open || isToday ? 'text-ink' : 'text-muted'} data-id="element-7482" data-mp="3ki8xs3zpuxss">
                      {h.day}
                      {isToday && <span className="ml-2 text-[13px] font-normal text-ink underline underline-offset-4" data-id="element-7483" data-mp="3phtwqeevz65i">vandaag</span>}
                    </dt>
                    <dd className={`tabular-nums ${open ? 'text-ink' : 'text-muted'}`} data-id="element-7484" data-mp="h3czu2ub4idr">{hoursLabel(h)}</dd>
                  </div>;
            })}
            </dl>
            <p className="mt-4 text-[13px] leading-relaxed text-muted" data-id="element-7485" data-mp="7axgp279xnra">
              {salon.name} · KvK {salon.kvk}
              {salon.vatId && <> · Btw-id {salon.vatId}</>}
            </p>
          </div>
        </div>
      </div>
    </Section>;
}